import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { colorStrings } from "@solveindustrial/Utilities/variables";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps extends WidgetProps {
    fields: {
        productTabLabel: string;
        contentTabLabel: string;
    };
}

interface QueryParams {
    query?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { isSearchPage, parameter, productFilters },
        },
    } = state;
    const location = getLocation(state);
    const searchPath = getSettingsCollection(state).searchSettings.searchPath;
    const productsDataView = getProductsDataView(state, parameter);
    const contentSearchPageURL = getPageLinkByPageType(state, "ContentSearchPage")?.url;

    const query = productFilters.query || parseQueryString<QueryParams>(location.search || "")?.query;

    return {
        isSearchPage,
        query,
        hasProductResults: productsDataView.value && productsDataView.value.length > 0,
        location,
        searchPath,
        contentSearchPageURL,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasHistory & ThemeProps<BaseTheme>;

export interface TabSectionStyles {
    wrapper?: InjectableCss;
    tabsContainer?: InjectableCss;
    select?: SelectPresentationProps;
    tabButton?: ButtonPresentationProps;
    tabButtonActive?: ButtonPresentationProps;
}

export const tabSectionStyles: TabSectionStyles = {
    wrapper: {
        css: css`
            margin: 20px 0;

            @media screen and (max-width: 768px) {
                margin: 15px 0;
            }
        `,
    },
    tabsContainer: {
        css: css`
            display: flex;
        `,
    },
    select: {
        cssOverrides: {
            formField: css`
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                align-self: stretch;
                min-height: 48px;
                color: ${getColor(colorStrings.textMain)};
                font-size: 16px;
                font-style: normal;
                font-weight: 700;
                line-height: 120%;
                width: 100%;
            `,
            inputSelect: css`
                min-height: 40px;
            `,
        },
    },
    tabButton: {
        color: colorStrings.commonGlobalGray04,
        typographyProps: {
            color: colorStrings.textMain,
        },
        css: css`
            min-width: 120px !important;

            &:focus {
                background-color: ${getColor(colorStrings.commonGlobalGray04)} !important;
                border-color: ${getColor(colorStrings.commonGlobalGray04)} !important;
                color: ${getColor(colorStrings.textMain)} !important;
                outline: solid 1px ${getColor(colorStrings.primaryMain)};
                outline-offset: 2px;
            }
        `,
        hoverStyle: {
            backgroundColor: `${getColor(colorStrings.commonGlobalGray04)} `,
            borderColor: `${getColor(colorStrings.commonGlobalGray04)}`,
            color: `${getColor(colorStrings.textMain)} `,
        },
        mergeCss: true,
    },
    tabButtonActive: {
        color: colorStrings.primaryMain,
        typographyProps: {
            color: colorStrings.commonGlobalWhite,
        },
        hoverMode: "lighten",
        // mergeCss: true,
    },
};

const styles = tabSectionStyles;

const TabSection = ({
    fields,
    query,
    location,
    history,
    searchPath,
    contentSearchPageURL,
    theme: { breakpoints },
}: Props) => {
    // Don't render anything if no query or no content search page URL is available
    if (!query || !contentSearchPageURL) {
        // eslint-disable-next-line no-console
        console.log("No query or no content search page URL", query, contentSearchPageURL);
        return null;
    }

    const [activeTab, setActiveTab] = useState("products");
    const [isMobile, setIsMobile] = useState(false);
    const isContentPage = location.pathname.toLowerCase().startsWith(contentSearchPageURL.toLowerCase());

    // Update active tab based on current URL
    useEffect(() => {
        setActiveTab(isContentPage ? "content" : "products");
    }, [location.pathname, isContentPage]);

    useEffect(() => {
        const handleWindowSize = () => {
            const maxMobileIdx = breakpoints.keys.indexOf("md");
            const maxMobile = breakpoints.values[maxMobileIdx];
            setIsMobile(window.innerWidth < maxMobile);
        };
        handleWindowSize();
        window.addEventListener("resize", debounce(handleWindowSize, 200));
    }, []);

    const productTabLabel = fields.productTabLabel || translate("Products");
    const contentTabLabel = fields.contentTabLabel || translate("Content");

    const handleTabChange = (tabKey: string) => {
        setActiveTab(tabKey);

        // Navigate to appropriate page
        if (tabKey === "content") {
            history.push(`${contentSearchPageURL}?query=${encodeURIComponent(query)}`);
        } else {
            history.push(`${searchPath}?query=${encodeURIComponent(query)}`);
        }
    };

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        handleTabChange(event.target.value);
    };

    if (isMobile) {
        return (
            <Select
                {...styles.select}
                value={activeTab}
                onChange={handleSelectChange}
                data-test-selector="search_tab_select"
                placeholder={activeTab === "products" ? `Viewing: ${productTabLabel}` : `Viewing: ${contentTabLabel}`}
            >
                <option value="products">{productTabLabel}</option>
                <option value="content">{contentTabLabel}</option>
            </Select>
        );
    }

    return (
        <StyledWrapper {...styles.tabsContainer}>
            <Button
                {...(activeTab === "products" ? styles.tabButtonActive : styles.tabButton)}
                onClick={() => handleTabChange("products")}
                data-test-selector="product_tab_button"
                css={css`
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;
                    min-width: 120px;
                `}
            >
                {productTabLabel}
            </Button>
            <Button
                {...(activeTab === "content" ? styles.tabButtonActive : styles.tabButton)}
                onClick={() => handleTabChange("content")}
                data-test-selector="content_tab_button"
                css={css`
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;
                    min-width: 120px;
                `}
            >
                {contentTabLabel}
            </Button>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(withHistory(TabSection))),
    definition: {
        displayName: "Search Results Tabs",
        allowedContexts: ["ProductListPage", "ContentSearchPage"],
        fieldDefinitions: [
            {
                name: "productTabLabel",
                editorTemplate: "TextField",
                defaultValue: "Products",
                fieldType: "Translatable",
                displayName: "Product Tab Label",
                isRequired: false,
            },
            {
                name: "contentTabLabel",
                editorTemplate: "TextField",
                defaultValue: "Content",
                fieldType: "Translatable",
                displayName: "Content Tab Label",
                isRequired: false,
            },
        ],
        group: "Products",
    },
};

export default widgetModule;
